import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { WorkforceOperationsNavComponent } from './workforce-operations-nav.component';

@Component({
  selector: 'app-admin-operations-report',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, WorkforceOperationsNavComponent],
  template: `
    <div class="min-h-full bg-[#eef3f8] p-4 md:p-5">
      <div class="flex flex-col xl:flex-row xl:items-end justify-between gap-3 mb-4">
        <div class="min-w-0">
          <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Workforce Operations</p>
          <h1 class="text-xl font-bold text-slate-950 tracking-tight leading-tight">Operations Command</h1>
          <p class="text-slate-500 text-[11px]">Action queue, workload, field productivity, cash, fleet, and service risk.</p>
        </div>
        <div class="flex flex-wrap items-end gap-2">
          <label class="block">
            <span class="block text-[10px] font-semibold text-slate-500 mb-1">Period</span>
            <select [(ngModel)]="filters.period" class="h-9 rounded-md border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-[#1A6FA6]">
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </label>
          <div class="flex items-end gap-1">
            <button type="button" (click)="setPreset('day')" class="h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-600 text-[11px] font-bold hover:text-[#1A6FA6]">Today</button>
            <button type="button" (click)="setPreset('week')" class="h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-600 text-[11px] font-bold hover:text-[#1A6FA6]">Week</button>
            <button type="button" (click)="setPreset('month')" class="h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-600 text-[11px] font-bold hover:text-[#1A6FA6]">Month</button>
          </div>
          <label class="block">
            <span class="block text-[10px] font-semibold text-slate-500 mb-1">From</span>
            <input type="date" [(ngModel)]="filters.from" class="h-9 rounded-md border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-[#1A6FA6]">
          </label>
          <label class="block">
            <span class="block text-[10px] font-semibold text-slate-500 mb-1">To</span>
            <input type="date" [(ngModel)]="filters.to" class="h-9 rounded-md border border-slate-200 bg-white px-3 text-[12px] text-slate-700 outline-none focus:border-[#1A6FA6]">
          </label>
          <button type="button" (click)="loadReport()" [disabled]="loading()" class="h-9 px-4 rounded-md bg-[#0C3A5A] text-white text-[11px] font-bold hover:bg-[#082d46] disabled:opacity-60 flex items-center gap-2">
            <i class="ti" [class.ti-loader]="loading()" [class.animate-spin]="loading()" [class.ti-refresh]="!loading()"></i>
            Refresh
          </button>
        </div>
      </div>

      <app-workforce-operations-nav active="command"></app-workforce-operations-nav>

      <div class="mb-4 rounded-lg border px-4 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3" [ngClass]="commandBannerClass()">
        <div>
          <p class="text-[10px] font-bold uppercase tracking-[0.12em]">Command status</p>
          <p class="text-[13px] font-bold mt-0.5">{{ healthLabel() }}</p>
          <p class="text-[11px] opacity-80 mt-1">Live exceptions are current; period metrics use {{ periodRangeLabel() }}.</p>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:min-w-[520px]">
          <div class="command-mini"><p>Risk</p><strong>{{ operationsRiskLabel() }}</strong></div>
          <div class="command-mini"><p>Queue</p><strong>{{ totalExceptions() }}</strong></div>
          <div class="command-mini"><p>Urgent</p><strong>{{ owner()?.urgent_open_jobs || 0 }}</strong></div>
          <div class="command-mini"><p>GPS gaps</p><strong>{{ owner()?.missing_shift_locations || 0 }}</strong></div>
        </div>
      </div>

      <div class="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-4">
        <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100">
            <h2 class="text-[12px] font-bold text-slate-900">Live now</h2>
            <p class="text-[10px] text-slate-500 mt-0.5">Current operational risk, independent of the selected date range.</p>
          </div>
          <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 p-3">
            <div class="metric-tile urgent"><p>Urgent open</p><strong>{{ owner()?.urgent_open_jobs || 0 }}</strong></div>
            <div class="metric-tile warning"><p>Unassigned</p><strong>{{ owner()?.unassigned_jobs || 0 }}</strong></div>
            <div class="metric-tile warning"><p>Paused</p><strong>{{ owner()?.paused_jobs || 0 }}</strong></div>
            <div class="metric-tile danger"><p>GPS missing</p><strong>{{ owner()?.missing_shift_locations || 0 }}</strong></div>
            <div class="metric-tile warning"><p>Fleet due</p><strong>{{ owner()?.fleet_due_30 || 0 }}</strong></div>
          </div>
        </section>

        <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100">
            <h2 class="text-[12px] font-bold text-slate-900">Selected period</h2>
            <p class="text-[10px] text-slate-500 mt-0.5">{{ periodRangeLabel() }} performance and cash movement.</p>
          </div>
          <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 p-3">
            <div class="metric-tile success"><p>Jobs completed</p><strong>{{ owner()?.jobs_completed || 0 }}</strong></div>
            <div class="metric-tile"><p>Started</p><strong>{{ owner()?.jobs_started || 0 }}</strong></div>
            <div class="metric-tile info"><p>Won work</p><strong>{{ (owner()?.quote_value_accepted || 0) | currency:'NZD':'symbol':'1.0-0' }}</strong></div>
            <div class="metric-tile warning"><p>Cash collected</p><strong>{{ (owner()?.cash_collected || 0) | currency:'NZD':'symbol':'1.0-0' }}</strong></div>
            <div class="metric-tile"><p>Shift hours</p><strong>{{ summary()?.total_shift_hours || 0 }}</strong></div>
          </div>
        </section>
      </div>

      <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden mb-4">
        <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div>
            <h2 class="text-[12px] font-bold text-slate-900">Priority action queue</h2>
            <p class="text-[10px] text-slate-500 mt-0.5">Start here before using the report tabs.</p>
          </div>
          <span class="px-2 py-1 rounded-md bg-rose-50 text-rose-700 text-[10px] font-bold">{{ totalExceptions() }} open</span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3">
          @for (card of exceptionCards(); track card.key) {
            <div class="rounded-lg border overflow-hidden" [ngClass]="card.items.length ? 'border-rose-100 bg-rose-50/30' : 'border-slate-100 bg-white'">
              <div class="px-3 py-2 border-b flex items-center justify-between gap-2" [ngClass]="card.items.length ? 'border-rose-100' : 'border-slate-100'">
                <div>
                  <h3 class="text-[12px] font-bold text-slate-900">{{ card.label }}</h3>
                  <p class="text-[10px] text-slate-500 mt-0.5">{{ card.hint }}</p>
                </div>
                <span class="pill" [class.!bg-rose-100]="card.items.length" [class.!text-rose-700]="card.items.length">{{ card.items.length }}</span>
              </div>
              <div class="p-3 space-y-2">
                @for (item of card.items.slice(0, 3); track exceptionKey(item)) {
                  <a [routerLink]="exceptionRoute(card.key, item)" class="block rounded-md border border-slate-100 bg-white px-3 py-2 hover:border-sky-200 hover:bg-sky-50/40 transition">
                    <div class="flex items-start justify-between gap-2">
                      <div class="min-w-0">
                        <p class="text-[11px] font-bold text-slate-900 truncate">{{ exceptionTitle(item) }}</p>
                        <p class="text-[10px] text-slate-500 mt-0.5 truncate">{{ exceptionDetail(item) }}</p>
                      </div>
                      <span class="text-[9px] font-bold text-[#1A6FA6] shrink-0">{{ exceptionActionLabel(card.key) }}</span>
                    </div>
                  </a>
                } @empty {
                  <p class="text-xs text-slate-400 py-4 text-center">Clear</p>
                }
              </div>
            </div>
          }
        </div>
      </section>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div class="focus-card">
          <p>Dispatch focus</p>
          <strong>{{ dispatchFocusLabel() }}</strong>
        </div>
        <div class="focus-card">
          <p>Cash focus</p>
          <strong>{{ cashFocusLabel() }}</strong>
        </div>
        <div class="focus-card">
          <p>Service focus</p>
          <strong>{{ serviceFocusLabel() }}</strong>
        </div>
      </div>

      @if (loading()) {
        <div class="bg-white rounded-lg border border-[#E7EEF5] p-10 text-center text-slate-500 text-sm">
          <i class="ti ti-loader animate-spin text-xl"></i>
          <p class="mt-2">Loading report...</p>
        </div>
      } @else {
        <div class="bg-white rounded-lg border border-[#E7EEF5] p-2 mb-3">
          <div class="flex flex-wrap gap-2">
            @for (tab of reportTabs; track tab.key) {
              <button
                type="button"
                (click)="activeTab.set(tab.key)"
                class="h-9 px-3 rounded-md text-[11px] font-bold border transition flex items-center gap-2"
                [ngClass]="activeTab() === tab.key ? 'bg-[#0C3A5A] text-white border-[#0C3A5A]' : 'bg-white text-slate-600 border-slate-200 hover:text-[#1A6FA6] hover:border-sky-200'"
              >
                <i class="ti" [ngClass]="tab.icon"></i>
                <span>{{ tab.label }}</span>
                <span class="min-w-5 px-1.5 py-0.5 rounded-full text-[10px]" [ngClass]="activeTab() === tab.key ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-500'">{{ tabCount(tab.key) }}</span>
              </button>
            }
          </div>
        </div>

        <div class="space-y-3">
          @if (activeTab() === 'overview') {
          <section class="grid grid-cols-1 xl:grid-cols-12 gap-3">
            <div class="xl:col-span-4 bg-white rounded-lg border border-[#E7EEF5] p-4">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <h2 class="text-[12px] font-bold text-slate-900">Operations command summary</h2>
                  <p class="text-[10px] text-slate-500 mt-0.5">{{ healthLabel() }}</p>
                </div>
                <span class="px-2 py-1 rounded-md text-[10px] font-bold" [ngClass]="healthBadgeClass()">{{ owner()?.ops_health_score ?? 100 }}/100</span>
              </div>
              <div class="mt-4 grid grid-cols-2 gap-2">
                <div class="metric-tile"><p>Active jobs</p><strong>{{ owner()?.active_jobs || 0 }}</strong></div>
                <div class="metric-tile"><p>Unassigned</p><strong>{{ owner()?.unassigned_jobs || 0 }}</strong></div>
                <div class="metric-tile"><p>Office handoff</p><strong>{{ owner()?.ready_for_office_jobs || 0 }}</strong></div>
                <div class="metric-tile"><p>Paused</p><strong>{{ owner()?.paused_jobs || 0 }}</strong></div>
              </div>
            </div>

            <div class="xl:col-span-4 bg-white rounded-lg border border-[#E7EEF5] p-4">
              <h2 class="text-[12px] font-bold text-slate-900">Revenue and cash control</h2>
              <p class="text-[10px] text-slate-500 mt-0.5">Quote conversion, invoicing, collections, and overdue exposure.</p>
              <div class="mt-4 space-y-3">
                <div class="flex justify-between gap-3"><span class="text-xs text-slate-500">Quote acceptance</span><strong class="text-xs text-slate-900">{{ owner()?.quote_acceptance_rate || 0 }}%</strong></div>
                <div class="flex justify-between gap-3"><span class="text-xs text-slate-500">Open quote value</span><strong class="text-xs text-[#1A6FA6]">{{ (owner()?.open_quote_value || 0) | currency:'NZD':'symbol':'1.0-0' }}</strong></div>
                <div class="flex justify-between gap-3"><span class="text-xs text-slate-500">Outstanding invoices</span><strong class="text-xs text-amber-700">{{ (owner()?.outstanding_balance || 0) | currency:'NZD':'symbol':'1.0-0' }}</strong></div>
                <div class="flex justify-between gap-3"><span class="text-xs text-slate-500">Overdue invoices</span><strong class="text-xs text-rose-700">{{ (owner()?.overdue_balance || 0) | currency:'NZD':'symbol':'1.0-0' }}</strong></div>
              </div>
            </div>

            <div class="xl:col-span-4 bg-white rounded-lg border border-[#E7EEF5] p-4">
              <h2 class="text-[12px] font-bold text-slate-900">Field execution control</h2>
              <p class="text-[10px] text-slate-500 mt-0.5">Worker visibility, hours, fleet readiness, and service quality.</p>
              <div class="mt-4 grid grid-cols-2 gap-2">
                <div class="metric-tile"><p>Shift hours</p><strong>{{ summary()?.total_shift_hours || 0 }}</strong></div>
                <div class="metric-tile"><p>GPS missing</p><strong>{{ owner()?.missing_shift_locations || 0 }}</strong></div>
                <div class="metric-tile"><p>Fleet due</p><strong>{{ owner()?.fleet_due_30 || 0 }}</strong></div>
                <div class="metric-tile"><p>Rating</p><strong>{{ owner()?.average_rating || 'N/A' }}</strong></div>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100">
              <h2 class="text-[12px] font-bold text-slate-900">Performance trend</h2>
              <p class="text-[10px] text-slate-500 mt-0.5">Workload, completion, labour hours, won quotes, and cash by selected grouping.</p>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full min-w-[760px] text-left text-[11px]">
                <thead class="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th class="px-4 py-3 table-head">Period</th>
                    <th class="px-3 py-3 table-head text-right">Jobs created</th>
                    <th class="px-3 py-3 table-head text-right">Completed</th>
                    <th class="px-3 py-3 table-head text-right">Urgent</th>
                    <th class="px-3 py-3 table-head text-right">Shift hours</th>
                    <th class="px-3 py-3 table-head text-right">Accepted quotes</th>
                    <th class="px-4 py-3 table-head text-right">Cash</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  @for (row of periodRows().slice(0, 12); track row.period_key) {
                    <tr class="hover:bg-slate-50">
                      <td class="px-4 py-3 font-bold text-slate-900">{{ row.period_label }}</td>
                      <td class="px-3 py-3 text-right">{{ row.jobs_created }}</td>
                      <td class="px-3 py-3 text-right text-emerald-700 font-semibold">{{ row.jobs_completed }}</td>
                      <td class="px-3 py-3 text-right text-rose-700">{{ row.urgent_created }}</td>
                      <td class="px-3 py-3 text-right">{{ row.shift_hours | number:'1.1-1' }}</td>
                      <td class="px-3 py-3 text-right text-[#1A6FA6]">{{ row.quotes_accepted_value | currency:'NZD':'symbol':'1.0-0' }}</td>
                      <td class="px-4 py-3 text-right text-amber-700">{{ row.cash_collected | currency:'NZD':'symbol':'1.0-0' }}</td>
                    </tr>
                  } @empty {
                    <tr><td colspan="7" class="empty-cell">No trend rows found.</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </section>

          }

          @if (activeTab() === 'shifts') {
          <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 class="text-[12px] font-bold text-slate-900">Plumber shift start / end</h2>
                <p class="text-[10px] text-slate-500 mt-0.5">First start and last end for each {{ periodLabel() }}.</p>
              </div>
              <span class="pill">{{ shiftRows().length }}</span>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full min-w-[700px] text-left text-[11px]">
                <thead class="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th class="px-4 py-3 table-head">Plumber</th>
                    <th class="px-3 py-3 table-head">Period</th>
                    <th class="px-3 py-3 table-head">Start</th>
                    <th class="px-3 py-3 table-head">End</th>
                    <th class="px-3 py-3 table-head text-right">Hours</th>
                    <th class="px-4 py-3 table-head text-right">Sessions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  @for (row of shiftRows(); track row.staff_id + row.period_key) {
                    <tr class="hover:bg-slate-50">
                      <td class="px-4 py-3">
                        <p class="font-bold text-slate-900">{{ row.staff_name }}</p>
                        <p class="text-[10px] text-slate-400">{{ row.staff_email }}</p>
                      </td>
                      <td class="px-3 py-3 font-semibold text-slate-700">{{ row.period_label }}</td>
                      <td class="px-3 py-3 text-slate-600">{{ formatTime(row.first_start) }}</td>
                      <td class="px-3 py-3 text-slate-600">{{ row.active ? 'Running' : formatTime(row.last_end) }}</td>
                      <td class="px-3 py-3 text-right font-bold text-slate-900">{{ row.total_hours | number:'1.2-2' }}</td>
                      <td class="px-4 py-3 text-right">
                        <span class="pill" [class.!bg-emerald-50]="row.active" [class.!text-emerald-700]="row.active">{{ row.session_count }}</span>
                      </td>
                    </tr>
                  } @empty {
                    <tr><td colspan="6" class="empty-cell">No shift rows found.</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </section>
          }

          @if (activeTab() === 'jobs') {
          <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 class="text-[12px] font-bold text-slate-900">Jobs per plumber</h2>
                <p class="text-[10px] text-slate-500 mt-0.5">Assigned jobs grouped by selected period.</p>
              </div>
              <span class="pill">{{ jobRows().length }}</span>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full min-w-[720px] text-left text-[11px]">
                <thead class="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th class="px-4 py-3 table-head">Plumber</th>
                    <th class="px-3 py-3 table-head">Period</th>
                    <th class="px-3 py-3 table-head text-right">Jobs</th>
                    <th class="px-3 py-3 table-head text-right">Started</th>
                    <th class="px-3 py-3 table-head text-right">Completed</th>
                    <th class="px-4 py-3 table-head">Status mix</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  @for (row of jobRows(); track row.staff_id + row.period_key) {
                    <tr class="hover:bg-slate-50">
                      <td class="px-4 py-3 font-bold text-slate-900">{{ row.staff_name }}</td>
                      <td class="px-3 py-3 font-semibold text-slate-700">{{ row.period_label }}</td>
                      <td class="px-3 py-3 text-right font-bold text-[#1A6FA6]">{{ row.job_count }}</td>
                      <td class="px-3 py-3 text-right text-slate-700">{{ row.started_count }}</td>
                      <td class="px-3 py-3 text-right text-emerald-700 font-semibold">{{ row.completed_count }}</td>
                      <td class="px-4 py-3 text-slate-500">{{ moduleSummary(row.by_status) }}</td>
                    </tr>
                  } @empty {
                    <tr><td colspan="6" class="empty-cell">No job rows found.</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </section>
          }

          @if (activeTab() === 'work-orders') {
          <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 class="text-[12px] font-bold text-slate-900">Work orders</h2>
                <p class="text-[10px] text-slate-500 mt-0.5">Created work orders by period.</p>
              </div>
              <span class="pill">{{ workOrderRows().length }}</span>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full min-w-[680px] text-left text-[11px]">
                <thead class="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th class="px-4 py-3 table-head">Period</th>
                    <th class="px-3 py-3 table-head text-right">Total</th>
                    <th class="px-3 py-3 table-head text-right">Urgent</th>
                    <th class="px-3 py-3 table-head text-right">Issued</th>
                    <th class="px-3 py-3 table-head text-right">Completed</th>
                    <th class="px-4 py-3 table-head">Created by</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  @for (row of workOrderRows(); track row.period_key) {
                    <tr class="hover:bg-slate-50">
                      <td class="px-4 py-3 font-bold text-slate-900">{{ row.period_label }}</td>
                      <td class="px-3 py-3 text-right font-bold text-amber-600">{{ row.work_order_count }}</td>
                      <td class="px-3 py-3 text-right text-rose-600">{{ row.urgent_count }}</td>
                      <td class="px-3 py-3 text-right text-slate-700">{{ row.issued_count }}</td>
                      <td class="px-3 py-3 text-right text-emerald-700">{{ row.completed_count }}</td>
                      <td class="px-4 py-3 text-slate-500">{{ moduleSummary(row.by_admin) }}</td>
                    </tr>
                  } @empty {
                    <tr><td colspan="6" class="empty-cell">No work orders found.</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </section>
          }

          @if (activeTab() === 'admin-activity') {
          <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 class="text-[12px] font-bold text-slate-900">Admin staff activity</h2>
                <p class="text-[10px] text-slate-500 mt-0.5">Created/updated records from modules with actor fields.</p>
              </div>
              <span class="pill">{{ activityRows().length }}</span>
            </div>
            <div class="overflow-x-auto">
              <table class="w-full min-w-[680px] text-left text-[11px]">
                <thead class="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th class="px-4 py-3 table-head">Admin</th>
                    <th class="px-3 py-3 table-head">Period</th>
                    <th class="px-3 py-3 table-head text-right">Actions</th>
                    <th class="px-4 py-3 table-head">Modules</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  @for (row of activityRows(); track row.user_id + row.period_key) {
                    <tr class="hover:bg-slate-50">
                      <td class="px-4 py-3 font-bold text-slate-900">{{ row.name }}</td>
                      <td class="px-3 py-3 font-semibold text-slate-700">{{ row.period_label }}</td>
                      <td class="px-3 py-3 text-right font-bold text-slate-900">{{ row.total_actions }}</td>
                      <td class="px-4 py-3 text-slate-500">{{ moduleSummary(row.modules) }}</td>
                    </tr>
                  } @empty {
                    <tr><td colspan="4" class="empty-cell">No admin activity found.</td></tr>
                  }
                </tbody>
              </table>
            </div>
          </section>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .table-head { font-size: 9px; font-weight: 700; color: rgb(148 163 184); text-transform: uppercase; letter-spacing: 0.08em; }
    .pill { display: inline-flex; align-items: center; justify-content: center; min-width: 1.75rem; padding: 0.125rem 0.5rem; border-radius: 999px; background: #f0f9ff; color: #1A6FA6; font-size: 10px; font-weight: 700; }
    .empty-cell { padding: 2rem 1rem; text-align: center; color: rgb(100 116 139); font-size: 12px; }
    .metric-tile { border: 1px solid #f1f5f9; background: #f8fafc; border-radius: 8px; padding: 0.625rem 0.75rem; min-height: 68px; }
    .metric-tile p { font-size: 10px; font-weight: 700; color: rgb(100 116 139); margin: 0; }
    .metric-tile strong { display: block; margin-top: 0.35rem; font-size: 18px; line-height: 1; color: rgb(15 23 42); }
    .focus-card { border: 1px solid #E7EEF5; background: #ffffff; border-radius: 8px; padding: 0.8rem 1rem; }
    .focus-card p { margin: 0; font-size: 10px; font-weight: 800; color: rgb(148 163 184); text-transform: uppercase; letter-spacing: 0.08em; }
    .focus-card strong { display: block; margin-top: 0.35rem; font-size: 12px; color: rgb(15 23 42); line-height: 1.35; }
    .command-mini { border-radius: 8px; background: rgba(255,255,255,0.72); border: 1px solid rgba(255,255,255,0.72); padding: 0.55rem 0.65rem; }
    .command-mini p { margin: 0; font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.7; }
    .command-mini strong { display: block; margin-top: 0.2rem; font-size: 16px; line-height: 1; }
    .metric-tile.success strong { color: rgb(4 120 87); }
    .metric-tile.info strong { color: #1A6FA6; }
    .metric-tile.warning strong { color: rgb(180 83 9); }
    .metric-tile.danger strong,
    .metric-tile.urgent strong { color: rgb(190 18 60); }
  `]
})
export class AdminOperationsReportComponent implements OnInit {
  private api = inject(ApiService);
  loading = signal(false);
  summary = signal<any>(null);
  owner = signal<any>(null);
  periodRows = signal<any[]>([]);
  exceptions = signal<Record<string, any[]>>({});
  shiftRows = signal<any[]>([]);
  jobRows = signal<any[]>([]);
  workOrderRows = signal<any[]>([]);
  activityRows = signal<any[]>([]);
  activeTab = signal<ReportTab>('overview');

  reportTabs: Array<{ key: ReportTab; label: string; icon: string }> = [
    { key: 'overview', label: 'Command Summary', icon: 'ti-chart-dots-3' },
    { key: 'shifts', label: 'Shifts', icon: 'ti-clock-hour-4' },
    { key: 'jobs', label: 'Jobs', icon: 'ti-tool' },
    { key: 'work-orders', label: 'Work Orders', icon: 'ti-clipboard-list' },
    { key: 'admin-activity', label: 'Admin Activity', icon: 'ti-user-cog' }
  ];

  filters = {
    period: 'day',
    from: this.todayIso(),
    to: this.todayIso()
  };

  ngOnInit() {
    this.loadReport();
  }

  loadReport() {
    if (!this.filters.from || !this.filters.to) {
      showToast('error', 'Select a date range.');
      return;
    }
    if (this.filters.from > this.filters.to) {
      showToast('error', 'From date cannot be after to date.');
      return;
    }

    this.loading.set(true);
    this.api.getAdminOperationsReport(this.filters).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res.success && res.data) {
          this.summary.set(res.data.summary || {});
          this.owner.set(res.data.owner_dashboard || {});
          this.periodRows.set(res.data.period_rows || []);
          this.exceptions.set(res.data.exceptions || {});
          this.shiftRows.set(res.data.shift_rows || []);
          this.jobRows.set(res.data.plumber_job_rows || []);
          this.workOrderRows.set(res.data.work_order_rows || []);
          this.activityRows.set(res.data.admin_activity_rows || []);
        } else {
          showToast('error', res.message || 'Report could not be loaded.');
        }
      },
      error: (err: any) => {
        this.loading.set(false);
        showToast('error', err?.error?.message || 'Report could not be loaded.');
      }
    });
  }

  periodLabel(): string {
    if (this.filters.period === 'week') return 'week';
    if (this.filters.period === 'month') return 'month';
    return 'day';
  }

  setPreset(kind: 'day' | 'week' | 'month') {
    const today = new Date();
    this.filters.period = kind;
    if (kind === 'day') {
      this.filters.from = this.toIso(today);
      this.filters.to = this.toIso(today);
    } else if (kind === 'week') {
      const start = new Date(today);
      const weekday = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - weekday);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      this.filters.from = this.toIso(start);
      this.filters.to = this.toIso(end);
    } else {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      this.filters.from = this.toIso(start);
      this.filters.to = this.toIso(end);
    }
    this.loadReport();
  }

  formatTime(value: string | null): string {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit', timeZone: 'Pacific/Auckland' });
  }

  moduleSummary(value: Record<string, number> | null | undefined): string {
    if (!value) return '-';
    const parts = Object.entries(value)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([label, count]) => `${label}: ${count}`);
    return parts.length ? parts.join(', ') : '-';
  }

  tabCount(tab: ReportTab): number {
    if (tab === 'overview') return this.totalExceptions();
    if (tab === 'jobs') return this.jobRows().length;
    if (tab === 'work-orders') return this.workOrderRows().length;
    if (tab === 'admin-activity') return this.activityRows().length;
    return this.shiftRows().length;
  }

  totalExceptions(): number {
    return Object.values(this.exceptions() || {}).reduce((sum, items: any) => sum + (Array.isArray(items) ? items.length : 0), 0);
  }

  exceptionCards(): Array<{ key: string; label: string; hint: string; items: any[] }> {
    const data = this.exceptions() || {};
    return [
      { key: 'urgent_unassigned_jobs', label: 'Urgent unassigned', hint: 'Jobs needing immediate allocation.', items: data['urgent_unassigned_jobs'] || [] },
      { key: 'paused_jobs', label: 'Paused jobs', hint: 'Field work stuck or waiting.', items: data['paused_jobs'] || [] },
      { key: 'overdue_invoices', label: 'Overdue invoices', hint: 'Cash collection risk.', items: data['overdue_invoices'] || [] },
      { key: 'expiring_quotes', label: 'Expiring quotes', hint: 'Follow-up before quote expiry.', items: data['expiring_quotes'] || [] },
      { key: 'fleet_compliance', label: 'Fleet compliance', hint: 'WOF/service due or overdue.', items: data['fleet_compliance'] || [] },
      { key: 'customer_followups', label: 'Customer follow-up', hint: 'Low feedback needing recovery.', items: data['customer_followups'] || [] }
    ];
  }

  exceptionRoute(key: string, item: any): any[] {
    if (key === 'urgent_unassigned_jobs' || key === 'paused_jobs') return ['/admin/dispatch/job', item.id];
    if (key === 'expiring_quotes') return ['/admin/financials/quotes', item.id];
    if (key === 'fleet_compliance') return ['/admin/operations/fleet'];
    if (key === 'customer_followups') return ['/admin/customer-feedback'];
    return ['/admin/operations/report'];
  }

  exceptionActionLabel(key: string): string {
    if (key === 'urgent_unassigned_jobs') return 'Allocate';
    if (key === 'paused_jobs') return 'Unblock';
    if (key === 'overdue_invoices') return 'Collect';
    if (key === 'expiring_quotes') return 'Follow up';
    if (key === 'fleet_compliance') return 'Check';
    if (key === 'customer_followups') return 'Recover';
    return 'Open';
  }

  exceptionKey(item: any): string {
    return String(item.id || item.invoice_no || item.quote_no || item.rego || item.reference_id || JSON.stringify(item));
  }

  exceptionTitle(item: any): string {
    return item.id || item.invoice_no || item.quote_no || item.rego || item.reference_id || item.vehicle_name || 'Item';
  }

  exceptionDetail(item: any): string {
    return item.address
      || item.customer_name
      || item.comments
      || item.valid_until
      || item.due_date
      || item.compliance_expiry
      || item.status
      || '-';
  }

  healthToneClass(): string {
    const score = Number(this.owner()?.ops_health_score ?? 100);
    if (score >= 85) return 'text-emerald-600';
    if (score >= 65) return 'text-amber-600';
    return 'text-rose-600';
  }

  healthBadgeClass(): string {
    const score = Number(this.owner()?.ops_health_score ?? 100);
    if (score >= 85) return 'bg-emerald-50 text-emerald-700';
    if (score >= 65) return 'bg-amber-50 text-amber-700';
    return 'bg-rose-50 text-rose-700';
  }

  healthLabel(): string {
    const score = Number(this.owner()?.ops_health_score ?? 100);
    if (score >= 85) return 'Operations are under control.';
    if (score >= 65) return 'Review the action queue before dispatch decisions.';
    return 'Immediate management attention required.';
  }

  operationsRiskLabel(): string {
    const score = Number(this.owner()?.ops_health_score ?? 100);
    if (score >= 85) return 'Low';
    if (score >= 65) return 'Watch';
    return 'High';
  }

  commandBannerClass(): string {
    const score = Number(this.owner()?.ops_health_score ?? 100);
    if (score >= 85) return 'bg-emerald-50 border-emerald-100 text-emerald-900';
    if (score >= 65) return 'bg-amber-50 border-amber-100 text-amber-900';
    return 'bg-rose-50 border-rose-100 text-rose-900';
  }

  periodRangeLabel(): string {
    return `${this.formatDate(this.filters.from)} - ${this.formatDate(this.filters.to)}`;
  }

  formatDate(value: string): string {
    if (!value) return '--';
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  dispatchFocusLabel(): string {
    const urgent = Number(this.owner()?.urgent_open_jobs || 0);
    const unassigned = Number(this.owner()?.unassigned_jobs || 0);
    const paused = Number(this.owner()?.paused_jobs || 0);
    if (urgent > 0) return `${urgent} urgent job${urgent === 1 ? '' : 's'} need allocation.`;
    if (unassigned > 0) return `${unassigned} unassigned job${unassigned === 1 ? '' : 's'} waiting.`;
    if (paused > 0) return `${paused} paused job${paused === 1 ? '' : 's'} to unblock.`;
    return 'Dispatch board is balanced.';
  }

  cashFocusLabel(): string {
    const overdue = Number(this.owner()?.overdue_balance || 0);
    const openQuotes = Number(this.owner()?.open_quote_value || 0);
    if (overdue > 0) return `${this.formatMoney(overdue)} overdue invoice balance.`;
    if (openQuotes > 0) return `${this.formatMoney(openQuotes)} open quote value to follow up.`;
    return 'No immediate cash exception.';
  }

  serviceFocusLabel(): string {
    const lowFeedback = Number(this.owner()?.low_feedback_open || 0);
    const missingGps = Number(this.owner()?.missing_shift_locations || 0);
    const fleetDue = Number(this.owner()?.fleet_due_30 || 0);
    if (lowFeedback > 0) return `${lowFeedback} customer follow-up${lowFeedback === 1 ? '' : 's'} open.`;
    if (missingGps > 0) return `${missingGps} active worker${missingGps === 1 ? '' : 's'} missing GPS.`;
    if (fleetDue > 0) return `${fleetDue} fleet compliance item${fleetDue === 1 ? '' : 's'} due soon.`;
    return 'Service risk is clear.';
  }

  private formatMoney(value: number): string {
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', maximumFractionDigits: 0 }).format(value);
  }

  private todayIso(): string {
    const today = new Date();
    return this.toIso(today);
  }

  private offsetIso(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return this.toIso(date);
  }

  private toIso(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}

type ReportTab = 'overview' | 'shifts' | 'jobs' | 'work-orders' | 'admin-activity';
